/**
 * The finished research, open for review, edit and copy — PRD §3.
 *
 * "Open the completed report in the existing editor, with a clear 'Back to
 * opportunities' control. Keep the list, brief, selected card, and current
 * report linked. The visitor can review, edit, and copy the report."
 *
 * The same Quill editor and the same helpers My Drafts uses —
 * `resolveSavedBriefResult`, `normalizeBriefBodyForEditor`,
 * `buildBriefCopyHtml`, `copyRichText`. Deliberately NOT `MyBriefsPage`
 * itself: that page is `RequireAuth` + `RequireBriefCreate` and assumes the
 * authenticated shell, neither of which a campaign visitor has.
 *
 * Sources and attribution live inside the generated body, per §3 — which is
 * also why they never appear on the opportunity row.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from 'primereact/editor';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { EVENTS, track } from '../../analytics/campaignAnalytics';
import {
  buildBriefCopyHtml,
  normalizeBriefBodyForEditor,
  resolveSavedBriefResult,
} from '../MyBriefs/myBriefsUtils';
import { copyRichText } from '../../utils/clipboard';
import { confineQuillTooltip } from '../../utils/quillLinkTooltip';
import { SHARED } from './audienceCopy';

/**
 * Matches My Drafts: the visitor is editing prose, not building a document.
 *
 * `formats` restricts what Quill RETAINS; it does not change what the toolbar
 * OFFERS. Without the matching `modules` and header below, the default
 * toolbar renders font, size, image and code-block controls whose output the
 * formats list then silently discards — buttons that appear to do nothing.
 */
const BODY_EDITOR_FORMATS = ['bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'link'];

const BODY_EDITOR_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean'],
  ],
};

const BODY_EDITOR_HEADER = (
  <>
    <span className="ql-formats">
      <button type="button" className="ql-bold" aria-label="Bold" />
      <button type="button" className="ql-italic" aria-label="Italic" />
      <button type="button" className="ql-underline" aria-label="Underline" />
      <button type="button" className="ql-strike" aria-label="Strike" />
    </span>
    <span className="ql-formats">
      <button type="button" className="ql-list" value="ordered" aria-label="Ordered list" />
      <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" />
      <button type="button" className="ql-blockquote" aria-label="Blockquote" />
    </span>
    <span className="ql-formats">
      <button type="button" className="ql-link" aria-label="Link" />
      <button type="button" className="ql-clean" aria-label="Clear formatting" />
    </span>
  </>
);

const AUTOSAVE_MS = 2000;

/**
 * Headlines wrap. A single-line input clipped this one at "…about their
 * collec" with no way to see the rest, which is a poor thing to do to the
 * first sentence of someone's draft.
 */
function GrowingField({ className, value, onChange, ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    />
  );
}

/**
 * `skin` chooses the class set — `cmp` for the brand-book design,
 * `mk` for Or's. The autosave, the editor configuration and the copy
 * helpers are real logic and are deliberately NOT duplicated into a second
 * component: two copies of an autosave loop is how one of them rots.
 */
export default function ReportView({ report, onBackToList, onSave, skin = 'cmp', aside = null }) {
  // An explicit map, not a prefix: the two stylesheets do not use parallel
  // names (`cmp-btn--secondary` vs `mk-btn--ghost`, `cmp-meta` vs
  // `mk-note`), and naive prefixing silently produced classes that do not
  // exist in either sheet.
  const mk = skin === 'mk';
  const C = {
    actions: mk ? 'mk-actions' : 'cmp-actions',
    ghostBtn: mk ? 'mk-btn mk-btn--ghost' : 'cmp-btn cmp-btn--secondary',
    note: mk ? 'mk-note' : 'cmp-meta',
    errorNote: mk ? 'mk-error' : 'cmp-meta cmp-meta--error',
    eyebrow: mk ? 'mk-eyebrow' : 'cmp-eyebrow',
    paper: mk ? 'mk-paper' : 'cmp-report',
    title: mk ? 'mk-paper-title' : 'cmp-report-header',
    standfirst: mk ? 'mk-lede' : 'cmp-report-subheader',
    layout: mk ? 'mk-report-layout' : 'cmp-report-layout',
    // PRD section 7.3. The Quill content area sits inside the paper element but
    // inherits none of its typography: `.mk-paper-body` is never applied to it,
    // so the report body has been rendering at Quill's snow defaults. This class
    // is the hook the stylesheets need to reach `.ql-editor`.
    bodyEditor: mk ? 'mk-paper-editor' : 'cmp-report-editor',
  };
  const resolved = useMemo(() => resolveSavedBriefResult(report), [report]);
  const [header, setHeader] = useState(resolved.content?.header || '');
  const [subheader, setSubheader] = useState(resolved.content?.subheader || '');
  const [body, setBody] = useState(() => normalizeBriefBodyForEditor(resolved.content?.brief || ''));
  const [copied, setCopied] = useState(false);
  const [saveState, setSaveState] = useState('idle');

  const timer = useRef(null);
  const hydrated = useRef(false);

  // Re-hydrate when a different report is opened. Without the id check this
  // would also fire on every poll of the SAME report and throw away whatever
  // the visitor was in the middle of typing.
  useEffect(() => {
    setHeader(resolved.content?.header || '');
    setSubheader(resolved.content?.subheader || '');
    setBody(normalizeBriefBodyForEditor(resolved.content?.brief || ''));
    hydrated.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.reportId]);

  useEffect(() => {
    if (!hydrated.current) { hydrated.current = true; return undefined; }
    if (timer.current) clearTimeout(timer.current);
    setSaveState('saving');
    timer.current = setTimeout(async () => {
      try {
        await onSave({ header, subheader, brief: body });
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [header, subheader, body, onSave]);

  const copy = useCallback(async () => {
    track(EVENTS.REPORT_COPY, {});
    const html = buildBriefCopyHtml({ header, subheader, body });
    await copyRichText({ html, text: `${header}\n\n${subheader}\n\n${body}` });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [header, subheader, body]);

  return (
    // The actions row spans BOTH columns. Inside the left one it pushed the
    // report down by its own height while the sign-up card beside it stayed
    // at the top, so the two columns started at different heights and read
    // as misaligned. The row belongs to the whole screen, not to the paper.
    <div className={`${C.layout}${aside ? '' : ` ${C.layout}--solo`}`}>
      <div className={mk ? 'mk-report-bar' : 'cmp-report-bar'}>
        {/* Constrained to the width of the paper column, so "Copy" lands on
            that column's right edge rather than floating in the middle of a
            row that spans the whole page. */}
        <div className={mk ? 'mk-report-bar-inner' : 'cmp-report-bar-inner'}>
          <div className={C.actions} style={{ marginTop: 0 }}>
            <button type="button" className={C.ghostBtn} onClick={onBackToList}>
              ← {SHARED.backToOpportunities}
            </button>
            <button type="button" className={`${C.ghostBtn} ${mk ? 'mk-copy' : 'cmp-copy'}`} onClick={copy}>
              <ContentCopyOutlinedIcon style={{ fontSize: 15 }} />
              {copied ? 'Copied' : 'Copy'}
            </button>
            {/* Reports the autosave without claiming one that has not happened —
                renders nothing at all while idle. */}
            {saveState === 'saving' && <span className={C.note}>Saving…</span>}
            {saveState === 'saved' && <span className={C.note}>Saved</span>}
            {saveState === 'error' && <span className={C.errorNote}>Not saved</span>}
          </div>
          <p className={C.eyebrow}>Research</p>
        </div>
      </div>

      <div className={mk ? 'mk-report-main' : 'cmp-report-main'}>
        {/* The eyebrow belongs to the bar row, not above the paper: left
            where it was it pushed the paper down by its own height and the
            two cards' top borders no longer lined up, which is the same
            fault the actions row had. It is a label for the screen. */}
        <div className={C.paper}>
          <GrowingField
            className={C.title}
            value={header}
            onChange={setHeader}
            ariaLabel="Headline"
          />
          <GrowingField
            className={C.standfirst}
            value={subheader}
            onChange={setSubheader}
            ariaLabel="Standfirst"
          />
          <Editor
            onLoad={confineQuillTooltip}
            className={C.bodyEditor}
            value={body}
            onTextChange={(e) => setBody(e.htmlValue || '')}
            headerTemplate={BODY_EDITOR_HEADER}
            modules={BODY_EDITOR_MODULES}
            formats={BODY_EDITOR_FORMATS}
            style={{ minHeight: 420 }}
          />
        </div>
      </div>

      {aside && <div className={mk ? 'mk-report-aside' : 'cmp-report-aside'}>{aside}</div>}
    </div>
  );
}
